import axios from 'axios';

// Create an Axios instance for non-streaming calls
const apiClient = axios.create({
  baseURL: '/api', // The proxy will handle redirecting this to the backend
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the JWT token in headers for apiClient
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('id_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default {
  // --- Auth ---
  getMe() {
    return apiClient.get('/auth/me');
  },
  createUser() {
    return apiClient.post('/auth/create_user');
  },

  // --- Organization CRUD ---
  getOrganizations() {
    return apiClient.get('/organizations');
  },
  createOrganization(organization) {
    return apiClient.post('/organizations', organization);
  },
  updateOrganization(id, organization) {
    return apiClient.put(`/organizations/${id}`, organization);
  },
  deleteOrganization(id) {
    return apiClient.delete(`/organizations/${id}`);
  },

  // --- User Management ---
  getUsersForOrganization(orgId, page = 1, perPage = 10, sortField = 'created_at', sortOrder = -1) {
    return apiClient.get(`/organizations/${orgId}/users`, {
      params: { 
        page, 
        per_page: perPage,
        sort_field: sortField,
        sort_order: sortOrder
      }
    });
  },
  bulkUpdateUsers(orgId, users) {
    return apiClient.put(`/organizations/${orgId}/users`, { users });
  },
  deleteUser(userId) {
    return apiClient.delete(`/users/${userId}`);
  },
  getRoles() {
    return apiClient.get('/roles');
  },

  // --- Domain Management ---
  getDomainsForOrganization(orgId) {
    return apiClient.get(`/organizations/${orgId}/domains`);
  },
  addDomainForOrganization(orgId, domainName) {
    return apiClient.post(`/organizations/${orgId}/domains`, { domain_name: domainName });
  },
  deleteDomain(domainId) {
    return apiClient.delete(`/domains/${domainId}`);
  },

  // --- Google Workspace Admin ---
  auditUsers(payload) {
    return apiClient.post('/google-workspace/audit/users', payload);
  },
  getTaskHistory() {
    return apiClient.get('/google-workspace/tasks');
  },
  getTask(taskId) {
    return apiClient.get(`/google-workspace/tasks/${taskId}`);
  },

  // --- Existing methods ---
  getConfig() {
    return apiClient.get('/config');
  },
  getMessage() {
    return apiClient.get('/message');
  },

  // --- Chatbot ---
  async streamChat(message, history, onUpdate) {
    const token = localStorage.getItem('id_token');

    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    let shouldReadStream = true; // Déclaré ici pour être accessible partout

    try {
      const response = await fetch('/api/jarvin', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ message, history }),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: `HTTP error! status: ${response.status}` };
        }
        shouldReadStream = false;
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (shouldReadStream) {
        const { value, done } = await reader.read();
        if (done) {
          onUpdate({ type: 'done' });
          shouldReadStream = false;
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process buffer for complete SSE messages
        let lastNewlineIndex;
        while ((lastNewlineIndex = buffer.indexOf('\n\n')) !== -1) {
          const eventString = buffer.substring(0, lastNewlineIndex).trim();
          buffer = buffer.substring(lastNewlineIndex + 2);

          if (eventString.startsWith('data:')) {
            const jsonString = eventString.substring(5).trim();
            if (jsonString === '[DONE]') {
              console.log('[API] Stream DONE');
              onUpdate({ type: 'done' });
              shouldReadStream = false;
              return;
            }
            try {
              const eventData = JSON.parse(jsonString);
              console.log('[API] Event received:', eventData.type, eventData); // Uncomment for verbose logs
              onUpdate(eventData);
            } catch (e) {
              console.error("Error parsing SSE JSON:", e, "String:", jsonString);
              onUpdate({ type: 'error', content: 'Erreur de parsing du flux.' });
              shouldReadStream = false;
            }
          }
        }
      }
    } catch (error) {
      console.error("Streaming API call failed:", error);
      onUpdate({ type: 'error', content: error.message || 'Échec de la connexion au chat.' });
      shouldReadStream = false;
    }
  },

  getSuggestions(history) {
    return apiClient.post('/jarvin/suggestions', { history });
  },
};
